import React from 'react';
import { createPortal } from 'react-dom';
import './auth.css';

/** Shared chrome for the login and invite pages (same tokens as the app: Warm paper + dark). */

export const primaryBtn =
  'w-full min-h-[52px] rounded-xl px-4 text-base font-semibold inline-flex items-center justify-center gap-3 ' +
  'bg-accent-strong text-txt-onAccent shadow-sm transition-colors hover:brightness-110 active:brightness-95 ' +
  'disabled:cursor-not-allowed disabled:opacity-60';

export const Spinner: React.FC = () => <span className="auth-spinner" aria-hidden="true" />;

export const Logo: React.FC<{ size?: number }> = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" role="img" aria-label="WISC-V" className="shrink-0">
    <rect width="64" height="64" rx="16" fill="var(--accent-strong)" />
    <path d="M14 20l8.5 26L32 26l9.5 20L50 20" fill="none" stroke="var(--text-on-accent)" strokeWidth="5"
      strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="32" cy="14" r="3" fill="var(--text-on-accent)" opacity="0.85" />
  </svg>
);

export const FaceIdIcon: React.FC = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M20 16v2a2 2 0 0 1-2 2h-2M8 20H6a2 2 0 0 1-2-2v-2" />
    <path d="M9 9.5v1.5M15 9.5v1.5M12 9.5v3.2a.8.8 0 0 1-.8.8H10.6M9 16c1.6 1.2 4.4 1.2 6 0" />
  </svg>
);

/** Official multicolor Google "G" (colors must not be changed). */
export const GoogleG: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

export type NoticeKind = 'info' | 'error' | 'success';
const NOTICE_STYLE: Record<NoticeKind, string> = {
  info: 'border-brd bg-surface-subtle text-txt-primary',
  success: 'border-accent-border bg-accent-soft text-txt-primary',
  error: 'border-[color:var(--error)] bg-surface-subtle text-txt-primary',
};
const NOTICE_ICON: Record<NoticeKind, string> = { info: 'ℹ', success: '✓', error: '!' };

export interface NoticeData { kind: NoticeKind; text: string }

/** Always-mounted polite live region, so screen readers announce messages as they appear. */
export const Notices: React.FC<{ items: (NoticeData | null | false | undefined)[] }> = ({ items }) => (
  <div aria-live="polite" className="space-y-2">
    {items.filter((n): n is NoticeData => !!n).map((n, i) => (
      <div key={n.text + i} role={n.kind === 'error' ? 'alert' : undefined}
        className={`auth-rise flex items-start gap-3 rounded-xl border px-3 py-2.5 text-sm ${NOTICE_STYLE[n.kind]}`}>
        <span aria-hidden="true" className={`mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full text-xs font-bold ${n.kind === 'error' ? 'bg-[color:var(--error)] text-surface-page' : 'bg-accent-strong text-txt-onAccent'}`}>
          {NOTICE_ICON[n.kind]}
        </span>
        <span>{n.text}</span>
      </div>
    ))}
  </div>
);

interface ShellProps {
  title: string;
  subtitle?: string;
  overlay?: boolean;
  children: React.ReactNode;
}

/**
 * overlay (session expired over a running app) is portalled to <body>: inside #root it sat under the
 * fullscreen result dialog (Radix, z-9990, portalled) and was aria-hidden/inert while that dialog was open.
 */
const AuthShell: React.FC<ShellProps> = ({ overlay, ...rest }) =>
  overlay ? createPortal(<ShellBody overlay {...rest} />, document.body) : <ShellBody {...rest} />;

const ShellBody: React.FC<ShellProps> = ({ title, subtitle, overlay, children }) => (
  <div className={`auth-page app-canvas ${overlay ? 'pointer-events-auto fixed inset-0 z-auth overflow-y-auto' : 'min-h-screen'} flex flex-col items-center justify-center px-4 py-8`}
    role={overlay ? 'dialog' : undefined} aria-modal={overlay || undefined} aria-labelledby="auth-title">
    <main className="auth-rise w-full max-w-sm">
      <div className="mb-6 flex flex-col items-center text-center">
        <Logo />
        <p className="mt-3 text-sm font-medium tracking-wide text-txt-secondary">WISC-V Diagnostyka</p>
      </div>
      <div className="glass-float rounded-2xl p-6">
        <h1 id="auth-title" className="text-2xl font-semibold text-txt-primary">{title}</h1>
        {subtitle && <p className="mt-2 text-sm text-txt-secondary">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
      <p className="mt-6 px-2 text-center text-xs leading-relaxed text-txt-secondary">
        Dane pacjentów chronione · dostęp tylko dla zaproszonych osób
      </p>
    </main>
  </div>
);

export default AuthShell;
